// ── useNip05Verified ──────────────────────────────────────────────
//
// Grants the BCHNostr verified badge. Returns true only when the
// authoritative BCHNostr NIP-05 directory confirms that the claimed
// name maps to this exact pubkey — which only happens after the user has
// paid the BCH verification fee and been added to the directory.
//
// Safe to gate badges on: it never returns true for an unconfirmed
// claim, so a user cannot mint a paid badge by editing their profile.

import { useState, useEffect } from 'react';
import {
  claimsBchnostrNip05,
  getVerificationStatus,
  verifyBchnostrNip05,
  onVerificationChange,
  getVerifiedNip05ForPubkey,
  isPubkeyVerifiedByDirectory,
  refreshDirectory,
  type VerificationStatus,
} from '../lib/nip05verify';

/**
 * Full verification status, for UI that needs to tell "not verified"
 * apart from "we can't reach the directory right now".
 */
export function useNip05Status(
  nip05: string | undefined | null,
  pubkey: string | undefined,
): VerificationStatus {
  const claims = claimsBchnostrNip05(nip05);

  const [status, setStatus] = useState<VerificationStatus>(
    () => (claims && pubkey ? getVerificationStatus(nip05, pubkey) : 'unverified'),
  );

  useEffect(() => {
    if (!claims || !pubkey) {
      setStatus('unverified');
      return;
    }

    let cancelled = false;
    const sync = () => {
      if (!cancelled) setStatus(getVerificationStatus(nip05, pubkey));
    };

    // Reflect whatever is already cached, immediately.
    sync();

    // Re-render when the directory refreshes, so a user who gets added
    // mid-session lights up without needing a reload.
    const unsubscribe = onVerificationChange(sync);

    // Ensure the directory is loaded (no-op when it's already fresh).
    verifyBchnostrNip05(nip05, pubkey).then(sync).catch(() => {});

    return () => { cancelled = true; unsubscribe(); };
  }, [nip05, pubkey, claims]);

  return status;
}

/**
 * Boolean form for badge rendering.
 *
 * `unknown` (directory unreachable and nothing cached) is treated as
 * not-verified for display purposes, so we never show an unearned badge.
 *
 * As well as the forward check (profile `nip05` → pubkey), this also
 * honours the directory in REVERSE: if the operator's directory / grant
 * assigns a @bchnostr.com handle to this pubkey, the badge stays even when
 * the profile's `nip05` field has been blanked by a later profile edit.
 * The directory entry is itself the proof of payment, so trusting it is
 * safe — the profile field is only ever an echo of it.
 */
export function useNip05Verified(
  nip05: string | undefined | null,
  pubkey: string | undefined,
): boolean {
  const claimVerified = useNip05Status(nip05, pubkey) === 'verified';

  // Reverse path: verified by the directory even if the profile no longer
  // carries the claim. Subscribes to directory changes so a mid-session
  // refresh lights the badge up without a reload.
  const [dirVerified, setDirVerified] = useState<boolean>(
    () => (pubkey ? isPubkeyVerifiedByDirectory(pubkey) : false),
  );

  useEffect(() => {
    if (!pubkey) { setDirVerified(false); return; }
    let cancelled = false;
    const sync = () => {
      if (!cancelled) setDirVerified(isPubkeyVerifiedByDirectory(pubkey));
    };
    sync();
    const unsubscribe = onVerificationChange(sync);
    // Make sure the directory is loaded so the reverse lookup has data.
    refreshDirectory().then(sync).catch(() => {});
    return () => { cancelled = true; unsubscribe(); };
  }, [pubkey]);

  return claimVerified || dirVerified;
}

/**
 * The verified @bchnostr.com handle for a pubkey, resolved from the
 * authoritative directory. Returns null when the pubkey isn't verified.
 *
 * Used as a display-name fallback: when a profile's kind 0 has a blank
 * `name`/`display_name`, we can still show the user's paid @bchnostr.com
 * handle instead of dropping to a raw npub.
 */
export function useVerifiedHandle(
  pubkey: string | undefined,
): string | null {
  const [handle, setHandle] = useState<string | null>(
    () => (pubkey ? getVerifiedNip05ForPubkey(pubkey) : null),
  );

  useEffect(() => {
    if (!pubkey) { setHandle(null); return; }
    let cancelled = false;
    const sync = () => {
      if (!cancelled) setHandle(getVerifiedNip05ForPubkey(pubkey));
    };
    sync();
    const unsubscribe = onVerificationChange(sync);
    refreshDirectory().then(sync).catch(() => {});
    return () => { cancelled = true; unsubscribe(); };
  }, [pubkey]);

  return handle;
}

/** A profile object as cached by useNostrQuery. Only the name fields matter here. */
interface NameProfile {
  name?: unknown;
  display_name?: unknown;
}

/** Trim-and-check that a profile field is a usable non-empty string. */
function usableName(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/**
 * Resolve the best display name for a user, everywhere in the app.
 *
 * Preference order:
 *   1. profile.display_name / profile.name (the normal case)
 *   2. the paid @bchnostr.com handle from the directory — so a profile
 *      that was re-published with a blank name still shows the user's
 *      real handle instead of a bare npub
 *   3. a shortened pubkey fallback
 *
 * `fallback` controls the last-resort rendering (call sites historically
 * used varying npub slice lengths and suffixes; pass whatever preserves
 * the existing look). When omitted it defaults to `pubkey.slice(0,8)…`.
 */
export function useDisplayName(
  pubkey: string | undefined,
  profile: NameProfile | null | undefined,
  fallback?: string,
): string {
  const handle = useVerifiedHandle(pubkey);

  const fromProfile = usableName(profile?.display_name) || usableName(profile?.name);
  if (fromProfile) return fromProfile;

  // Directory handle (without the domain) reads well as a name.
  if (handle) return handle.split('@')[0];

  if (fallback !== undefined) return fallback;
  return pubkey ? pubkey.slice(0, 8) + '…' : '';
}
